import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import Header from '../components/Header';
import Footer from '../components/Footer';
import InfoboxSchutz from '../components/InfoboxSchutz';
import { useAppContext } from '../context/AppContext';
import { applicationService } from '../backend/applicationService';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { de } from 'date-fns/locale';
import { useYupValidation } from '../hooks/useYupValidation';
import * as yup from 'yup';
import GlobalErrorAlert from '../components/GlobalErrorAlert';
import { useGlobalError } from '../context/GlobalErrorContext';
import { isoNamingForSexToAnredeMap } from '../util/helper';

const validationRules = yup.object().shape({
  doiCode: yup
    .string()
    .required('Bitte Code eingeben')
    .matches(/^\d+$/, 'Der Code darf nur Ziffern enthalten')
    .length(9, 'Der Code muss 9 Ziffern lang sein.'),
});

function EMailBestaetigung() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const { state, dispatch } = useAppContext();

  const [doiCode, setDoiCode] = useState(searchParams.get('doiCode') || '');
  const [loading, setLoading] = useState(false);
  const [autoProcessing, setAutoProcessing] = useState(false);
  const { errors, fieldRefs, setError, validateField, validateForm, scrollAndFocusErrorField } =
    useYupValidation(validationRules);
  const { showGlobalError, clearGlobalError } = useGlobalError();

  const handleAutoAcceptDoi = useRef(false);

  const reinitializeState = useCallback(
    (applicationId, application, email) => {
      const formData = {};
      formData.geschlecht = isoNamingForSexToAnredeMap[application.applicant.sex] ?? '';
      formData.vorname = application.applicant.firstname;
      formData.nachname = application.applicant.surname;
      formData.geburtsdatum = new Date(application.applicant.birthdate);
      formData.staatsangehoerigkeit = application.applicant.nationalityCode;
      formData.plz = application.applicant.address.zip;
      formData.ort = application.applicant.address.city;
      formData.strasse = application.applicant.address.street;
      formData.hausnummer = application.applicant.address.streetNo;
      formData.email = application.applicant.contact[0].data;
      formData.telefonnummer = application.applicant.contact[1] ? application.applicant.contact[1].data : '';
      formData.versicherungssumme = application.chosenProductsAndOptions[0].scope[0].value;
      formData.selectedOptions = application.chosenProductsAndOptions[0].options.map(o => o.code);
      formData.versicherungsbeginn = new Date(application.modalities.beginDate);
      formData.zahlungsintervall = application.modalities.period.code;
      if (searchParams.get('email')) formData.fromMail = true;

      dispatch({ type: 'SET_FORM_DATA', payload: formData });
      dispatch({ type: 'SET_SELECTED_PRODUCT', payload: application.chosenProductsAndOptions[0] });
      dispatch({ type: 'SET_APPLICATION_ID', payload: applicationId });
      dispatch({ type: 'SET_QUALIFIED_APPLICATION', payload: { qualifiedApplication: application } });
      dispatch({
        type: 'SET_APPLICATION_DATA',
        payload: {
          applicationObject: application,
          applicationId: applicationId,
          email: email,
        },
      });
    },
    [dispatch, searchParams],
  );

  const logInWithDoiCode = useCallback(
    async (applicationId, doiCode, email) => {
      setLoading(true);
      clearGlobalError();

      const response = await applicationService.acceptOptIn(applicationId, doiCode, email, state.sessionId);
      if (response.success) {
        reinitializeState(applicationId, response.data, email);
        dispatch({ type: 'SET_CURRENT_STEP', payload: 4 });

        navigate('/deine-zahlungsdaten');
      } else {
        // for some reason the api returns 404, if doi code is wrong
        if (response.status === 404) {
          setError('doiCode', 'Code ist nicht gültig oder abgelaufen.');
          scrollAndFocusErrorField('doiCode');
        } else {
          showGlobalError('Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
        }
      }

      setLoading(false);
    },
    [
      dispatch,
      navigate,
      setError,
      showGlobalError,
      state.sessionId,
      clearGlobalError,
      reinitializeState,
      scrollAndFocusErrorField,
    ],
  );

  useEffect(() => {
    // this small hack is necessary to avoid unexpected behaviour in local development, that is caused by React Strict Mode
    // acceptOptIn API is not idempotent, that's why first api call in dev is successful, second (made by strict mode) fails
    if (handleAutoAcceptDoi.current) {
      return;
    }
    handleAutoAcceptDoi.current = true;

    (async () => {
      if (searchParams.size === 0) {
        return;
      }
      const doiCodeParam = searchParams.get('doiCode');
      const sessionIdParam = searchParams.get('sessionId');
      const emailParam = searchParams.get('email');

      if (!doiCodeParam || !sessionIdParam || !emailParam) {
        return;
      }

      dispatch({ type: 'SET_APPLICATION_ID', payload: sessionIdParam });
      dispatch({ type: 'SET_FORM_DATA', payload: { email: emailParam } });

      setAutoProcessing(true);
      await logInWithDoiCode(sessionIdParam, doiCodeParam, emailParam);

      setAutoProcessing(false);
    })();
  }, [searchParams, dispatch, logInWithDoiCode]);

  const handleDoiCodeChange = event => {
    const value = event.target.value.toUpperCase();
    setDoiCode(value);
  };

  const handleBlur = async event => {
    const { name, value } = event.target;
    await validateField(name, value);
  };

  const handleSubmit = async event => {
    event.preventDefault();

    if (!(await validateForm({ doiCode: doiCode }))) {
      return;
    }
    await logInWithDoiCode(state.applicationId, doiCode, state.formData.email);
  };

  if (autoProcessing) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <Header />
        <Divider sx={{ borderColor: theme.palette.secondary.main }} aria-hidden="true" />

        <Container maxWidth="md" sx={{ flex: 1, py: 8, textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant="h5" gutterBottom>
            E-Mail wird automatisch bestätigt...
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Bitte warten Sie einen Moment.
          </Typography>
        </Container>

        <Footer />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
      <Box className="wrapper" role="main" aria-label="Bestätigung der E-Mail-Adresse">
        <Container className="main-content-scroll-container">
          <Box className="main-content-with-sidebar">
            {/* --- PAGE CONTENT  --- */}
            <Box className="page-content">
              <Box component="form" role="form" onSubmit={handleSubmit} noValidate>
                <Typography variant="h1" className="headline-large-bold">
                  E-Mail Bestätigung
                </Typography>

                <p style={{ marginTop: '16px' }}> Mit * gekennzeichnete Felder sind Pflichtfelder. </p>

                <GlobalErrorAlert sx={{ mt: 1 }} />

                <Box sx={{ mt: 2, mb: 4 }}>
                  <Box variant="body2" component="p" sx={{ mt: 4, mb: 2 }}>
                    Wir haben eine E-Mail an die von dir angegebene E-Mail-Adresse gesandt. Die E-Mail beinhaltet die
                    Erstinformationen, bestehend aus folgenden vorvertraglichen Unterlagen:
                    Versicherungs&shy;bedingungen und Kundeninformation, Produkt&shy;informationsblatt,
                    Vertrags&shy;informationen inkl. Widerrufs&shy;belehrung, Datenschutzerklärung.
                  </Box>
                  <Box variant="body2" component="p" sx={{ mt: 3, mb: 2 }}>
                    In der E-Mail befindet sich ein Bestätigungsbutton, über den du zurück zum Abschluss der R+V
                    Hardwareversicherung gelangst.
                  </Box>
                  <Box variant="body2" component="p" sx={{ mt: 3, mb: 2 }}>
                    Weiterhin ist in der E-Mail ein Code enthalten, mit dem du die Beantragung hier einfach fortsetzen
                    kannst.
                  </Box>
                </Box>

                <TextField
                  inputRef={fieldRefs.doiCode}
                  id="doi-code-field"
                  name="doiCode"
                  variant="filled"
                  className="input-field larger-input-text"
                  fullWidth
                  label="Code eingeben"
                  value={doiCode}
                  onChange={handleDoiCodeChange}
                  onBlur={handleBlur}
                  placeholder="ABC123DEF"
                  required
                  error={!!errors.doiCode}
                  helperText={errors.doiCode || '9-stelliger Code aus der E-Mail'}
                  aria-describedby="doi-code-field-helper-text" // Verknüpfung mit der Fehler-ID
                  autoComplete="off"
                  inputProps={{
                    maxLength: 9,
                    style: {
                      textAlign: 'center',
                      fontSize: '1.2rem',
                      letterSpacing: '0.2em',
                      textTransform: 'uppercase',
                    },
                    'aria-invalid': !!errors.doiCode, // Zugänglichkeits-Attribut für Screenreader
                  }}
                  sx={{
                    mb: 3,
                    '& .MuiInputBase-input::placeholder': {
                      color: 'var(--color-text-disabled)', // z.B. '#999999' oder 'rgba(0,0,0,0.5)'
                      opacity: 1,
                    },
                  }}
                  slotProps={{
                    formHelperText: { 'data-testid': 'doiCode-error' },
                  }}
                />

                {/* Aria-live-Region für Fehler */}
                <div
                  id="error-live-region"
                  aria-live="assertive"
                  style={{
                    position: 'absolute', // Unsichtbar für visuelle Nutzer, aber lesbar
                    width: '1px',
                    height: '1px',
                    overflow: 'hidden',
                    clip: 'rect(0 0 0 0)',
                    whiteSpace: 'nowrap',
                    border: 0,
                  }}
                >
                  {errors.doiCode && `Fehler: ${errors.doiCode}`}
                </div>

                <Box variant="body2" component="p" sx={{ mt: 0, mb: 2 }}>
                  Bitte prüfe auch deinen SPAM-Ordner, falls die Unterlagen in den nächsten Minuten nicht bei dir
                  eintreffen.
                </Box>

                {/* Navigation Buttons */}
                <Box className="button-container" sx={{ mt: 4 }}>
                  <Button
                    className="button-general button-action"
                    variant="contained"
                    type="submit"
                    aria-disabled={loading || doiCode.length !== 9} // WCAG-konform deaktiviert
                    disabled={false} // Nie vollständig deaktivieren, Tab-Stopp bleibt erhalten
                    aria-label="Zahlungsdaten angeben"
                  >
                    Zahlungsdaten angeben
                  </Button>
                </Box>

                {/* Sticky InfoboxSchutz für Desktop */}
                {isDesktop && (
                  <div className="infobox-sidebar-container">
                    <InfoboxSchutz />
                  </div>
                )}
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
    </LocalizationProvider>
  );
}

export default EMailBestaetigung;
